import { useState } from "react";
import { CharacterSpecies, EquipmentBase, OutfitBlock, Trait, WeaponBlock, WeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Button, FormGroup, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";

import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import AbilityEditor from "../../characterPage/AbilityEditor";
import Page from "../../../components/Page";
import ButtonFlat from "../../../components/ButtonFlat";
import { CreateDefaultAttributes, CreateDefaultCombatSkills, CreateDefaultPrimarySkills, CreateDefaultSpaceSkills } from "../../../master/data";
import StatLookupField from "../../../components/StatLookupField";
import { AllStats } from "../../../model/statsConstant";
import { NumberField } from "../../../components/formFields/NumberField";
import DeleteIcon from '@mui/icons-material/Delete';

const defaultSpecies: CharacterSpecies = {
    id: "",
    description: "",
    adjustments: [],
    strongHits: [],
    name: "New Trait",
    color: "#3c61b9",
    image: "",
}

const defaultAttribtues = CreateDefaultAttributes();
const defaultPrimarySkills = CreateDefaultPrimarySkills();
const defaultSpaceSkills = CreateDefaultSpaceSkills();
const defaultCombatSkills = CreateDefaultCombatSkills();

export const GetNew = (name: string): CharacterSpecies => {
    return {
        ...defaultSpecies,
        id: v4(),
        name: name
    }
}

export const SpeciesEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<CharacterSpecies>(loadLibraryData<CharacterSpecies>("lspec", id ?? "NONE") ?? GetNew("New Species"));

    const handleSave = () => {
        navigate(`/library/species/${current.id}`, {replace: true});
        saveLibraryData("lspec", current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, "lspec");
    }

    const onChange = (key: string, value: any) => {
        setCurrent((c) => ({
            ...c,
            [key]: value
        }))
    }

    const handleAddAdjustment = () => {
        setCurrent({
            ...current,
            adjustments: [...current.adjustments, {
                adjustment: 0,
                targetId: ""
            }]
        })
    }

    const handleAdjustmentTarget = (index: number, id: string) => {
        const skills = current.adjustments;
        skills[index].targetId = id;
        setCurrent({ ...current, adjustments: skills });
    }

    const handleChangeAdjustmentValue = (index: number, adjustment: number) => {
        const skills = current.adjustments;
        skills[index].adjustment = adjustment;
        setCurrent({ ...current, adjustments: skills });
    }

    const handleRemoveAdjustment = (index:number) => {
        const skills = [ ...current.adjustments]; 
        skills.splice(index, 1);
        setCurrent({ ...current, adjustments: skills })
    }

    return <Page>
        <h2>{current.name}</h2>
        <FormGroup>
            <FormFieldLabel label="Name">
                <TextField type="text" variant="standard" value={current.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            <Box marginLeft={1} marginTop={1}>
                <Typography>Description</Typography>
                <TextField value={current.description} onChange={(event) => onChange('description', event.currentTarget.value)} multiline fullWidth />
            </Box>
            <Box marginLeft={1} marginTop={1}>
                <Typography>Adjustments</Typography>
                {current.adjustments.map((skill, index) => {
                    return <Grid key={index} container boxSizing="border-box">
                        <Grid item xs>
                            <StatLookupField onChange={(value) => handleAdjustmentTarget(index, value)} value={skill.targetId} stats={[
                                ...defaultPrimarySkills,
                                ...defaultCombatSkills,
                                ...defaultSpaceSkills,
                                ...defaultAttribtues,
                                ...defaultAttribtues.map(a => ({ id: `${a.id}_max`, name: `${a.name} Max` })),
                                ...AllStats
                            ]} />
                        </Grid>
                        <Grid item>
                            <NumberField step={1} value={skill.adjustment} onChange={(value) => handleChangeAdjustmentValue(index, value)} />
                        </Grid>
                        <Grid item>
                            <IconButton onClick={() => handleRemoveAdjustment(index)} ><DeleteIcon /></IconButton>
                        </Grid>
                    </Grid>
                })}
                <Button onClick={handleAddAdjustment}>Add Skill Adjustment</Button>
            </Box>
            <AbilityEditor abilities={current.strongHits} setAbilities={(value) => onChange('strongHits', value)} />
            <Stack direction={"row"} spacing={1} paddingTop={1}>
                <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
            </Stack>
        </FormGroup>
    </Page>
}